/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { ACCOUNT_SETTINGS_PAGES } from '../../../routing/routeConfiguration';
import { FormattedMessage } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import { ensureCurrentUser } from '../../../util/data';

import { AvatarLarge, InlineTextButton, NamedLink, NotificationBadge } from '../../../components';

import css from './TopbarMobileMenu.module.css';

const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    currentUserHasListings,
    currentUser,
    notificationCount,
    onLogout,
    handleApplyForDiscountModal,
  } = props;

  const user = ensureCurrentUser(currentUser);

  const activePage = page => {
    return currentPage === page ? css.activeLink : null;
  };

  const NavLink = () => {
    return (
      <>
        <NamedLink
          // className={css.createListingLink}
          className={classNames(css.createListingLink, activePage('HomePage'))}
          name="HomePage"
        >
          <span className={css.createListing}>
            <FormattedMessage id="TopbarDesktop.homePage" />
          </span>
        </NamedLink>
        <NamedLink
          className={classNames(css.createListingLink, activePage('SearchPage'))}
          name="SearchPage"
        >
          <span className={css.createListing}>
            <FormattedMessage id="TopbarDesktop.therapistDir" />
          </span>
        </NamedLink>
        <NamedLink
          className={classNames(css.createListingLink, activePage('FreeConsultation'))}
          name="FreeConsultation"
        >
          <span className={css.createListing}>
            <FormattedMessage id="TopbarDesktop.bookFree" />
          </span>
        </NamedLink>
        <NamedLink
          className={classNames(css.createListingLink, activePage('contact-us'))}
          name="CMSPage"
          params={{ pageId: 'contact-us' }}
          showSearchParams={false}
        >
          <span className={css.createListing}>
            <FormattedMessage id="TopbarDesktop.contactUs" />
          </span>
        </NamedLink>
        <span
          className={css.applyForDiscount}
          onClick={e => {
            e.preventDefault();
            handleApplyForDiscountModal && handleApplyForDiscountModal(true);
          }}
        >
          <FormattedMessage id="TopbarDesktop.applyForDiscount" />
        </span>

        {!isAuthenticated ? (
          <NamedLink
            params={{ is_login: 'true' }}
            name="LoginPage"
            className={classNames(css.createListingLink, activePage('LoginPage'))}
          >
            <span className={css.createListing}>
              <FormattedMessage id="TopbarMobileMenu.loginLink" />
            </span>
          </NamedLink>
        ) : null}
      </>
    );
  };

  if (!isAuthenticated) {
    const signup = (
      <NamedLink name="SignupPage" className={css.signupLink}>
        <FormattedMessage id="TopbarMobileMenu.signupLink" />
      </NamedLink>
    );

    const login = (
      <NamedLink name="LoginPage" className={css.loginLink}>
        <FormattedMessage id="TopbarMobileMenu.loginLink" />
      </NamedLink>
    );

    const signupOrLogin = (
      <span className={css.authenticationLinks}>
        <FormattedMessage id="TopbarMobileMenu.signupOrLogin" values={{ signup, login }} />
      </span>
    );
    return (
      <div className={css.root}>
        <div className={css.content}>
          <NavLink />
          {/* <div className={css.authenticationGreeting}>
            <FormattedMessage
              id="TopbarMobileMenu.unauthorizedGreeting"
              values={{ lineBreak: <br />, signupOrLogin }}
            />
          </div> */}
        </div>
        {/* <div className={css.footer}>
          <NamedLink className={css.createNewListingLink} name="NewListingPage">
            <FormattedMessage id="TopbarMobileMenu.newListingLink" />
          </NamedLink>
        </div> */}
      </div>
    );
  }

  const notificationCountBadge =
    notificationCount > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={notificationCount} />
    ) : null;

  const displayName = user.attributes.profile.firstName;
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  return (
    <div className={css.root}>
      <div className={css.content}>
        <NavLink />
        <AvatarLarge className={css.avatar} user={currentUser} />
        <span className={css.greeting}>
          <FormattedMessage id="TopbarMobileMenu.greeting" values={{ displayName }} />
        </span>
        <NamedLink
          className={classNames(css.inbox, currentPageClass('InboxPage'), activePage('InboxPage'))}
          name="InboxPage"
          params={{ tab: currentUserHasListings ? 'sales' : 'orders' }}
        >
          <FormattedMessage id="TopbarMobileMenu.inboxLink" />
          {notificationCountBadge}
        </NamedLink>
        {/* <NamedLink
          className={classNames(css.navigationLink, currentPageClass('ManageListingsPage'))}
          name="ManageListingsPage"
        >
          <FormattedMessage id="TopbarMobileMenu.yourListingsLink" />
        </NamedLink> */}

        {currentUserHasListings ? (
          <NamedLink
            className={classNames(
              css.navigationLink,
              currentPageClass('ProfileSettingsPage'),
              activePage('ProfileSettingsPage')
            )}
            name="ProfileSettingsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />
          </NamedLink>
        ) : null}

        <NamedLink
          className={classNames(
            css.navigationLink,
            currentPageClass('AccountSettingsPage'),
            activePage('ContactDetailsPage'),
            activePage('PasswordChangePage')
          )}
          name="AccountSettingsPage"
        >
          <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
        </NamedLink>
        <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
          <FormattedMessage id="TopbarMobileMenu.logoutLink" />
        </InlineTextButton>
        {/* <div className={css.spacer} /> */}
      </div>
      {/* <div className={css.footer}>
        <NamedLink className={css.createNewListingLink} name="NewListingPage">
          <FormattedMessage id="TopbarMobileMenu.newListingLink" />
        </NamedLink>
      </div> */}
    </div>
  );
};

TopbarMobileMenu.defaultProps = { currentUser: null, notificationCount: 0, currentPage: null };

const { bool, func, number, string } = PropTypes;

TopbarMobileMenu.propTypes = {
  isAuthenticated: bool.isRequired,
  currentUserHasListings: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: string,
  notificationCount: number,
  onLogout: func.isRequired,
};

export default TopbarMobileMenu;
